import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { usePolling } from '../hooks/usePolling.js';
import { POLL_INTERVAL, STORAGE_KEY } from '../lib/constants.js';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [snapshot, setSnapshot] = useState({
    items: [],
    skyads: [],
    ads: [],
    shiftLogs: [],
    audits: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [token, setTokenState] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [managerAuthed, setManagerAuthed] = useState(
    () => Boolean(localStorage.getItem(STORAGE_KEY))
  );

  // Coalesce snapshot fetches: while a refresh is in flight, further requests
  // (from the poller, or explicit post-write calls) collapse into a single
  // trailing re-run instead of spamming the server with overlapping requests.
  const refreshState = useRef({ inFlight: false, queued: false });

  const refresh = useCallback(() => {
    const st = refreshState.current;
    if (st.inFlight) {
      st.queued = true; // re-run once after the in-flight fetch settles
      return;
    }
    st.inFlight = true;
    (async () => {
      try {
        const data = await api.snapshot(token);
        setSnapshot({
          items: Array.isArray(data.items) ? data.items : [],
          skyads: Array.isArray(data.skyads) ? data.skyads : [],
          ads: Array.isArray(data.ads) ? data.ads : [],
          shiftLogs: Array.isArray(data.shiftLogs) ? data.shiftLogs : [],
          audits: Array.isArray(data.audits) ? data.audits : [],
        });
        setError(null);
        setLastSync(new Date());
      } catch (e) {
        setError(e.message);
      } finally {
        const s2 = refreshState.current;
        s2.inFlight = false;
        setLoading(false);
        if (s2.queued) {
          s2.queued = false;
          refresh();
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Live real-time sync across connected clients. Slowed to 20s, paused while
  // the tab is hidden/backgrounded, jittered, and non-overlapping (Tier 1+2).
  usePolling(refresh, {
    intervalMs: POLL_INTERVAL,
    enabled: true,
    pauseWhenHidden: true,
  });

  const pushToast = useCallback((message, accent = 'teal') => {
    const id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    setToasts((t) => [...t, { id, message, accent }]);
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, 2400);
  }, []);

  const setToken = useCallback((t) => {
    setTokenState(t);
    if (t) {
      localStorage.setItem(STORAGE_KEY, t);
      setManagerAuthed(true);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      setManagerAuthed(false);
    }
  }, []);

  const logout = useCallback(async () => {
    if (token) {
      try {
        await api.logoutManager(token);
      } catch {
        /* ignore — best-effort server-side invalidation */
      }
    }
    setToken('');
  }, [token, setToken]);

  const value = useMemo(
    () => ({
      snapshot,
      loading,
      error,
      lastSync,
      toasts,
      pushToast,
      refresh,
      token,
      managerAuthed,
      setToken,
      logout,
    }),
    [snapshot, loading, error, lastSync, toasts, pushToast, refresh, token, managerAuthed, setToken, logout]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside <AppProvider>');
  return ctx;
}